import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'environments/environment';
import { forkJoin, interval, takeWhile } from 'rxjs';
import { WellSchematic, WellSchematicCdft } from '../../model';
import { WellSchematicDiagramService } from '../../services/well-schematic-diagram.service';
import { WellSchematicDiagramState } from '../../state/well-schematic-diagram.state';
import { ModuleFederationToolsModule } from '@angular-architects/module-federation-tools';
import module from 'app/modules/well-schematic-diagram/well-schematic-diagram.module';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { Location } from '@angular/common';

am4core.useTheme(am4themes_animated);
am4core.useTheme(am4themes_dark);

@Component({
  selector: 'app-dashboard-view',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [...module, ModuleFederationToolsModule],
  templateUrl: './dashboard-view.page.html',
  styleUrl: './dashboard-view.page.scss'
})
export class DashboardViewPage {
  isApplication: boolean;
  applicationId: string;
  webComponent;
  wellStringId;
  schematic: WellSchematic;
  cdftSchematic: WellSchematicCdft[];
  isFindChartId: boolean = false
  isLoading: boolean = false
  aprCategory: string = ""
  activityDate: string = ""


  mawop = {
    annAPress: 0,
    annBPress: 0,
    annCPress: 0,
    annDPress: 0
  }

  aprMeter = {
    annAPress: 0,
    annBPress: 0,
    annCPress: 0,
    annDPress: 0
  }

  constructor(
    private route: ActivatedRoute,
    private service: WellSchematicDiagramService,
    private state: WellSchematicDiagramState,
    private elementRef: ElementRef,
    private location: Location
  ) { }

  async ngOnInit() {
    this.isLoading = true
    this.isApplication =
      this.route.snapshot.paramMap.get('type') != 'configuration';
    this.applicationId = this.route.snapshot.paramMap.get('type');
    this.wellStringId = this.route.snapshot.paramMap.get('wellStringId');
    this.schematic = await this.getSchematic(this.wellStringId);
    this.initiateComponent();

    this.getMawop2()
    this.getApr()

    interval(1000)
      .pipe(
        takeWhile(() => !this.isFindChartId)
      )
      .subscribe(() => {
        console.log("Condition still false → retrying…");
        this.isFindChartId = document.getElementById("chartAnullusA") !== null || document.getElementById("chartAnullusC") !== null;

        // do your retry logic here
        if (this.isFindChartId) {
          // Create Gauge Chart
          this.chartAnnulusA()
          this.chartAnnulusB()
          this.chartAnnulusC()
          this.chartAnnulusD()
        }
      });
  }

  getSchematic(wellStringId, force = false): Promise<WellSchematic> {
    return new Promise((resolve, reject) => {
      if (!this.state.isEmpty() && !force) {
        resolve(this.state.findByWellStringId(wellStringId));
      }

      this.service
        .getWellConfigList({ Search: { wellStringId } })
        .subscribe((response) => {
          resolve(response.data.data[0]);
        });
    });
  }

  getMawop2() {
    this.service.aprMawopGaugeMeter(this.wellStringId).subscribe((response) => {
      let data = response.data
      this.mawop.annAPress = data.annAPress
      this.mawop.annBPress = data.annBPress
      this.mawop.annCPress = data.annCPress
      this.mawop.annDPress = data.annDPress
    })
  }

  getApr() {
    this.service.aprGaugeMeter(this.wellStringId).subscribe((response) => {
      let data = response.data
      this.aprCategory = data.category
      this.activityDate = data.updatedDate
      this.aprMeter.annAPress = data.annAPress
      this.aprMeter.annBPress = data.annBPress
      this.aprMeter.annCPress = data.annCPress
      this.aprMeter.annDPress = data.annDPress
    })
  }

  initiateComponent() {
    this.webComponent = {
      options: {
        type: 'script',
        remoteEntry: `${environment.mfeReactUrl}`,
        remoteName: 'react',
        exposedModule: './well-barrier-details',
        elementName: 'well-barrier-details',
      },
      props: {
        isView: true,
      },
      events: {
        create: (e) => this.create(e.detail),
        getAllConfig: (e) => this.getAllConfig(e.detail),
        update: (e) => this.update(e.detail),
        delete: (e) => this.delete(e.detail),
        assign: (e) => this.assign(e.detail),
        status: (e) => this.status(e.detail),
        submit: (e) => this.submit(e.detail),
        getCdft: (e) => this.getCdft(e.detail),
        getAnnulus: (e) => this.getAnnulus(e.detail),
        getMawop: (e) => this.getMawop(e.detail),
        previousPage: (e) => { this.location.back() }


      },
    };
  }

  status(event) {
    const payload = {
      ...event.payload,
      id: this.applicationId,
      remark: '',
      wellStringId: this.wellStringId,
    };
    this.service.saveApplicationStatus(payload).subscribe((response) => {
      event.resolve(response);
    });
  }

  create(event) {
    this.service.saveWellConfig(event.payload).subscribe((response) => {
      event.resolve(response);
    });
  }

  update(event) {
    this.service.updateWellConfig(event.payload).subscribe((response) => {
      event.resolve(response);
    });
  }

  delete(event) {
    this.service
      .deleteWellConfig(event.payload.id)
      .subscribe((response) => {
        event.resolve(response);
      });
  }

  assign(event) {
    this.service
      .assignSchematic(this.schematic.wellStringId, event.payload.id)
      .subscribe(async (response) => {
        this.schematic = await this.getSchematic(
          this.wellStringId,
          true
        );
        event.resolve(response);
      });
  }

  getAllConfig(event) {
    const params = {
      wellStringId: this.wellStringId,
      isApplication: this.isApplication ? 'true' : 'false',
    };
    const allConfigsParams = {
      PageSize: event.payload?.PageSize ?? '',
      Page: event.payload?.Page ?? '',
      TotalPage: event.payload?.totalPages ?? '',
      Search: event.payload?.Search ?? {},
    };
    forkJoin({
      selected: this.service.getWellSchematicByWellString(params),
      all: this.service.getAllConfig(allConfigsParams),
    }).subscribe(({ selected, all }) => {
      const configs = {
        selectedConfig: selected.data,
        allConfig: all.data,
        isApplication: this.isApplication,
        schematic: this.schematic,
        isView: true,
      };
      event.resolve(configs);
    });
  }

  getCdft(event) {
    // if (this.cdftSchematic) {
    //   return event.resolve(this.cdftSchematic);
    // }

    this.service
      .getCdftSchematic(this.schematic?.wellStringId ?? this.wellStringId)
      .subscribe((response) => {
        event.resolve(response.data);
        this.isLoading = false
      });
  }

  getConfigurationHistory(event) {
    this.service
      .getConfigurationHistory(this.schematic.wellStringId)
      .subscribe((response) => {
        event.resolve(response.data);
      });
  }

  getAnnulus(event) {
    this.service
      .aprGaugeMeter(this.schematic.wellStringId)
      .subscribe((response) => {
        event.resolve(response.data);
      });
  }

  getMawop(event) {
    this.service
      .aprMawopGaugeMeter(this.schematic.wellStringId)
      .subscribe((response) => {
        event.resolve(response.data);
      });
  }

  submit(event) {
    const payload = {
      wellStringId: this.wellStringId,
      ...event.payload,
    };

    this.service
      .submitWellSchematicByString(payload)
      .subscribe((response) => {
        event.resolve(response);
      });
  }


  chartAnnulusA() {
    if (!this.mawop.annAPress || this.mawop.annAPress === 0) {
      this.noMawopChart("chartAnullusA", this.aprMeter.annAPress)
      return
    }

    let chartMin = 0;
    let chartMax = this.aprMeter.annAPress > this.mawop.annAPress + 500 ? this.aprMeter.annAPress : this.mawop.annAPress + 500
    let eighttyPercent = this.mawop.annAPress * 0.8;

    let data = {
      score: this.aprMeter.annAPress ?? 0,
      activityDate: handleFormatDate(this.activityDate, true) || '',
      fromAprCat: this.aprCategory,
      gradingData: [
        {
          title: "Healthy",
          color: "#7fd26cff",
          lowScore: -1,
          highScore: 1000
        },
        {
          title: "Healthy Medium Risk",
          color: "#f3eb0c",
          lowScore: 1000,
          highScore: eighttyPercent
        },
        {
          title: "Unhealthy High Risk",
          color: "#fdae19",
          lowScore: eighttyPercent,
          highScore: this.mawop.annAPress
        },
        {
          title: "Unhealthy Very High Risk",
          color: "#ee1f25",
          lowScore: this.mawop.annAPress,
          highScore: chartMax
        },
      ]
    };

    /**
    Grading Lookup
     */
    function lookUpGrade(lookupScore, grades) {
      // Only change code below this line
      for (var i = 0; i < grades.length; i++) {
        if (
          grades[i].lowScore < lookupScore &&
          grades[i].highScore >= lookupScore
        ) {
          return grades[i];
        }
      }
      return null;
    }

    // create chart
    let chart = am4core.create("chartAnullusA", am4charts.GaugeChart);
    chart.hiddenState.properties.opacity = 0;
    chart.fontSize = 7;
    chart.innerRadius = am4core.percent(80);
    chart.resizable = true;

    /**
     * Normal axis
     */

    const axis = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis.min = chartMin;
    axis.max = chartMax;
    axis.strictMinMax = true;
    axis.renderer.radius = am4core.percent(80);
    axis.renderer.inside = true
    axis.renderer.line.strokeOpacity = 0.1;
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.strokeOpacity = 1;
    axis.renderer.ticks.template.strokeWidth = 0.5;
    axis.renderer.ticks.template.length = 5;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = am4core.percent(15);
    axis.renderer.labels.template.fontSize = "0.9em";
    axis.renderer.labels.template.fill = am4core.color("#000");


    /**
     * Axis for ranges
     */

    const axis2 = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis2.min = chartMin;
    axis2.max = chartMax;
    axis2.strictMinMax = true;
    axis2.renderer.labels.template.disabled = true;
    axis2.renderer.ticks.template.disabled = true;
    axis2.renderer.grid.template.disabled = false;
    axis2.renderer.grid.template.opacity = 0.5;
    axis2.renderer.labels.template.bent = true;
    axis2.renderer.labels.template.fill = am4core.color("#000");
    axis2.renderer.labels.template.fontWeight = "bold";
    axis2.renderer.labels.template.fillOpacity = 0.3;


    /**
    Ranges
    */

    for (let grading of data.gradingData) {
      let range = axis2.axisRanges.create();
      range.axisFill.fill = am4core.color(grading.color);
      range.axisFill.fillOpacity = 0.8;
      range.axisFill.zIndex = -1;
      range.value = grading.lowScore > chartMin ? grading.lowScore : chartMin;
      range.endValue = grading.highScore < chartMax ? grading.highScore : chartMax;
      range.grid.strokeOpacity = 0;
      (range as any).stroke = am4core.color(grading.color).lighten(-0.1);
      // range.label.inside = true;
      // range.label.text = grading.title.toUpperCase();
      // range.label.inside = true;
      // range.label.location = 0.5;
      // range.label.inside = true;
      // (range.label as any).radius = am4core.percent(10);
      // range.label.paddingBottom = -5; // ~half font size
      // range.label.fontSize = "0.9em";
    }

    let matchingGrade = lookUpGrade(data.score, data.gradingData);

    /**
     * Label 1
     */

    let label = chart.radarContainer.createChild(am4core.Label);
    label.isMeasured = false;
    label.fontSize = "1.4rem";
    label.x = am4core.percent(50);
    label.paddingBottom = 37;
    label.horizontalCenter = "middle";
    label.verticalCenter = "bottom";
    label.text = data.score.toFixed(0);
    label.fill = am4core.color(matchingGrade.color);

    /**
     * psiLabel
     */

    let psiLabel = chart.radarContainer.createChild(am4core.Label);
    psiLabel.isMeasured = false;
    psiLabel.fontSize = "1em";
    psiLabel.paddingBottom = 31;
    psiLabel.horizontalCenter = "middle";
    psiLabel.verticalCenter = "bottom";
    psiLabel.text = "PSI"
    psiLabel.fill = am4core.color(matchingGrade.color);

    /**
     * Label 2
     */

    let label2 = chart.radarContainer.createChild(am4core.Label);
    label2.isMeasured = false;
    label2.fontSize = "0.7rem";
    label2.paddingBottom = 15;
    label2.horizontalCenter = "middle";
    label2.verticalCenter = "bottom";
    label2.text = data.fromAprCat?.toUpperCase();
    label2.fill = am4core.color(matchingGrade.color);

    /**
     * Label 3
     */

    let label3 = chart.radarContainer.createChild(am4core.Label);
    label3.isMeasured = false;
    label3.fontSize = "0.7rem";
    label3.horizontalCenter = "middle";
    label3.verticalCenter = "bottom";
    label3.text = data.activityDate;
    label3.fill = am4core.color(matchingGrade.color);

    window.addEventListener("resize", function () {
      let vw = window.innerWidth;

      if (vw > 1920) {
        label.fontSize = "1.6rem";
        psiLabel.fontSize = "1.3em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      } else {
        label.fontSize = "1.4rem";
        psiLabel.fontSize = "1em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      }
    });

    /**
     * Hand
     */

    let hand = chart.hands.push(new am4charts.ClockHand());
    hand.axis = axis2;
    hand.innerRadius = am4core.percent(55);
    hand.startWidth = 8;
    hand.pin.disabled = true;
    // hand.value = data.score;
    hand.fill = am4core.color("#444");
    hand.stroke = am4core.color("#000");



    setTimeout(() => {
      hand.showValue(data.score, 1000, am4core.ease.cubicOut); // animated
    }, 1000);

    removeAmChartLogo(this.elementRef.nativeElement);
  }

  chartAnnulusB() {
    if (!this.mawop.annBPress || this.mawop.annBPress === 0) {
      this.noMawopChart("chartAnullusB", this.aprMeter.annBPress)
      return
    }

    let chartMin = 0;
    let chartMax = this.aprMeter.annBPress > this.mawop.annBPress ? this.aprMeter.annBPress : this.mawop.annBPress + 500;
    let eighttyPercentAnnB = this.mawop.annBPress * 0.8;

    let data = {
      score: this.aprMeter.annBPress ?? 0,
      updatedDate: handleFormatDate(this.activityDate, true) || '',
      fromAprCat: this.aprCategory,
      gradingData: [
        {
          title: "Healthy",
          color: "#7fd26cff",
          lowScore: -1,
          highScore: 500
        },
        {
          title: "Healthy Medium Risk",
          color: "#f3eb0c",
          lowScore: 500,
          highScore: eighttyPercentAnnB
        },
        {
          title: "Unhealthy High Risk",
          color: "#fdae19",
          lowScore: eighttyPercentAnnB,
          highScore: this.mawop.annBPress
        },
        {
          title: "Unhealthy Very High Risk",
          color: "#ee1f25",
          lowScore: this.mawop.annBPress,
          highScore: chartMax
        },
      ]
    };

    /**
    Grading Lookup
     */
    function lookUpGrade(lookupScore, grades) {
      // Only change code below this line
      for (var i = 0; i < grades.length; i++) {
        if (
          grades[i].lowScore < lookupScore &&
          grades[i].highScore >= lookupScore
        ) {
          return grades[i];
        }
      }
      return null;
    }

    // create chart
    let chart = am4core.create("chartAnullusB", am4charts.GaugeChart);
    chart.hiddenState.properties.opacity = 0;
    chart.fontSize = 7;
    chart.innerRadius = am4core.percent(80);
    chart.resizable = true;

    /**
     * Normal axis
     */

    const axis = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis.min = chartMin;
    axis.max = chartMax;
    axis.strictMinMax = true;
    axis.renderer.radius = am4core.percent(80);
    axis.renderer.inside = true;
    axis.renderer.line.strokeOpacity = 0.1;
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.strokeOpacity = 1;
    axis.renderer.ticks.template.strokeWidth = 0.5;
    axis.renderer.ticks.template.length = 5;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = am4core.percent(15);
    axis.renderer.labels.template.fontSize = "0.9em";
    axis.renderer.labels.template.fill = am4core.color("#000");


    /**
     * Axis for ranges
     */

    const axis2 = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis2.min = chartMin;
    axis2.max = chartMax;
    axis2.strictMinMax = true;
    axis2.renderer.labels.template.disabled = true;
    axis2.renderer.ticks.template.disabled = true;
    axis2.renderer.grid.template.disabled = false;
    axis2.renderer.grid.template.opacity = 0.5;
    axis2.renderer.labels.template.bent = true;
    axis2.renderer.labels.template.fill = am4core.color("#000");
    axis2.renderer.labels.template.fontWeight = "bold";
    axis2.renderer.labels.template.fillOpacity = 0.3;


    /**
    Ranges
    */

    for (let grading of data.gradingData) {
      let range = axis2.axisRanges.create();
      range.axisFill.fill = am4core.color(grading.color);
      range.axisFill.fillOpacity = 0.8;
      range.axisFill.zIndex = -1;
      range.value = grading.lowScore > chartMin ? grading.lowScore : chartMin;
      range.endValue = grading.highScore < chartMax ? grading.highScore : chartMax;
      range.grid.strokeOpacity = 0;
      (range as any).stroke = am4core.color(grading.color).lighten(-0.1);
      // range.label.inside = true;
      // range.label.text = grading.title.toUpperCase();
      // range.label.inside = true;
      // range.label.location = 0.5;
      // range.label.inside = true;
      // (range.label as any).radius = am4core.percent(10);
      // range.label.paddingBottom = -5; // ~half font size
      // range.label.fontSize = "0.9em";
    }

    let matchingGrade = lookUpGrade(data.score, data.gradingData);

    /**
     * Label 1
     */

    let label = chart.radarContainer.createChild(am4core.Label);
    label.isMeasured = false;
    label.fontSize = "1.4rem";
    label.x = am4core.percent(50);
    label.paddingBottom = 37;
    label.horizontalCenter = "middle";
    label.verticalCenter = "bottom";
    label.text = data.score?.toFixed(0);
    label.fill = am4core.color(matchingGrade.color);

    /**
   * psiLabel
   */

    let psiLabel = chart.radarContainer.createChild(am4core.Label);
    psiLabel.isMeasured = false;
    psiLabel.fontSize = "1em";
    psiLabel.paddingBottom = 31;
    psiLabel.horizontalCenter = "middle";
    psiLabel.verticalCenter = "bottom";
    psiLabel.text = "PSI"
    psiLabel.fill = am4core.color(matchingGrade.color);

    /**
     * Label 2
     */

    let label2 = chart.radarContainer.createChild(am4core.Label);
    label2.isMeasured = false;
    label2.fontSize = "0.7rem";
    label2.paddingBottom = 15;
    label2.horizontalCenter = "middle";
    label2.verticalCenter = "bottom";
    label2.text = data.fromAprCat.toUpperCase();
    label2.fill = am4core.color(matchingGrade.color);

    /**
     * Label 3
     */

    let label3 = chart.radarContainer.createChild(am4core.Label);
    label3.isMeasured = false;
    label3.fontSize = "0.7rem";
    label3.horizontalCenter = "middle";
    label3.verticalCenter = "bottom";
    label3.text = data.updatedDate;
    label3.fill = am4core.color(matchingGrade.color);

    window.addEventListener("resize", function () {
      let vw = window.innerWidth;

      if (vw > 1920) {
        label.fontSize = "1.6rem";
        psiLabel.fontSize = "1.3em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      } else {
        label.fontSize = "1.4rem";
        psiLabel.fontSize = "1em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      }
    });

    /**
     * Hand
     */

    let hand = chart.hands.push(new am4charts.ClockHand());
    hand.axis = axis2;
    hand.innerRadius = am4core.percent(55);
    hand.startWidth = 8;
    hand.pin.disabled = true;
    // hand.value = data.score;
    hand.fill = am4core.color("#444");
    hand.stroke = am4core.color("#000");

    setTimeout(() => {
      hand.showValue(data.score, 1000, am4core.ease.cubicOut); // animated
    }, 1500);

    removeAmChartLogo(this.elementRef.nativeElement);
  }

  chartAnnulusC() {
    if (!this.mawop.annCPress || this.mawop.annCPress === 0) {
      this.noMawopChart("chartAnullusC", this.aprMeter.annCPress)
      return
    }

    let chartMin = 0;
    let chartMax = this.aprMeter.annCPress > this.mawop.annCPress + 500 ? this.aprMeter.annCPress : this.mawop.annCPress + 500;
    let eighttyPercentAnnB = this.mawop.annCPress * 0.8;

    let data = {
      score: this.aprMeter.annCPress ?? 0,
      updatedDate: handleFormatDate(this.activityDate, true) || '',
      fromAprCat: this.aprCategory ?? '',
      gradingData: [
        {
          title: "Healthy",
          color: "#7fd26cff",
          lowScore: -1,
          highScore: 200
        },
        {
          title: "Healthy Medium Risk",
          color: "#f3eb0c",
          lowScore: 200,
          highScore: eighttyPercentAnnB
        },
        {
          title: "Unhealthy High Risk",
          color: "#fdae19",
          lowScore: eighttyPercentAnnB,
          highScore: this.mawop.annCPress
        },
        {
          title: "Unhealthy Very High Risk",
          color: "#ee1f25",
          lowScore: this.mawop.annCPress,
          highScore: chartMax
        },
      ]
    };

    /**
    Grading Lookup
     */
    function lookUpGrade(lookupScore, grades) {
      // Only change code below this line
      for (var i = 0; i < grades.length; i++) {
        if (
          grades[i].lowScore < lookupScore &&
          grades[i].highScore >= lookupScore
        ) {
          return grades[i];
        }
      }
      return null;
    }

    // create chart
    let chart = am4core.create("chartAnullusC", am4charts.GaugeChart);
    chart.hiddenState.properties.opacity = 0;
    chart.fontSize = 7;
    chart.innerRadius = am4core.percent(80);
    chart.resizable = true;

    /**
     * Normal axis
     */

    const axis = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis.min = chartMin;
    axis.max = chartMax;
    axis.strictMinMax = true;
    axis.renderer.radius = am4core.percent(80);
    axis.renderer.inside = true;
    axis.renderer.line.strokeOpacity = 0.1;
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.strokeOpacity = 1;
    axis.renderer.ticks.template.strokeWidth = 0.5;
    axis.renderer.ticks.template.length = 5;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = am4core.percent(15);
    axis.renderer.labels.template.fontSize = "0.9em";
    axis.renderer.labels.template.fill = am4core.color("#000");

    /**
     * Axis for ranges
     */

    const axis2 = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis2.min = chartMin;
    axis2.max = chartMax;
    axis2.strictMinMax = true;
    axis2.renderer.labels.template.disabled = true;
    axis2.renderer.ticks.template.disabled = true;
    axis2.renderer.grid.template.disabled = false;
    axis2.renderer.grid.template.opacity = 0.5;
    axis2.renderer.labels.template.bent = true;
    axis2.renderer.labels.template.fill = am4core.color("#000");
    axis2.renderer.labels.template.fontWeight = "bold";
    axis2.renderer.labels.template.fillOpacity = 0.3;


    /**
    Ranges
    */

    for (let grading of data.gradingData) {
      let range = axis2.axisRanges.create();
      range.axisFill.fill = am4core.color(grading.color);
      range.axisFill.fillOpacity = 0.8;
      range.axisFill.zIndex = -1;
      range.value = grading.lowScore > chartMin ? grading.lowScore : chartMin;
      range.endValue = grading.highScore < chartMax ? grading.highScore : chartMax;
      range.grid.strokeOpacity = 0;
      (range as any).stroke = am4core.color(grading.color).lighten(-0.1);
      // range.label.inside = true;
      // range.label.text = grading.title.toUpperCase();
      // range.label.inside = true;
      // range.label.location = 0.5;
      // range.label.inside = true;
      // (range.label as any).radius = am4core.percent(10);
      // range.label.paddingBottom = -5; // ~half font size
      // range.label.fontSize = "0.9em";
    }

    let matchingGrade = lookUpGrade(data.score, data.gradingData);

    /**
     * Label 1
     */

    let label = chart.radarContainer.createChild(am4core.Label);
    label.isMeasured = false;
    label.fontSize = "1.4rem";
    label.x = am4core.percent(50);
    label.paddingBottom = 37;
    label.horizontalCenter = "middle";
    label.verticalCenter = "bottom";
    label.text = data.score.toFixed(0);
    label.fill = am4core.color(matchingGrade.color);


    /**
   * psiLabel
   */

    let psiLabel = chart.radarContainer.createChild(am4core.Label);
    psiLabel.isMeasured = false;
    psiLabel.fontSize = "1em";
    psiLabel.paddingBottom = 31;
    psiLabel.horizontalCenter = "middle";
    psiLabel.verticalCenter = "bottom";
    psiLabel.text = "PSI"
    psiLabel.fill = am4core.color(matchingGrade.color);

    /**
     * Label 2
     */

    let label2 = chart.radarContainer.createChild(am4core.Label);
    label2.isMeasured = false;
    label2.fontSize = "0.7rem";
    label2.paddingBottom = 15;
    label2.horizontalCenter = "middle";
    label2.verticalCenter = "bottom";
    label2.text = data.fromAprCat.toUpperCase();
    label2.fill = am4core.color(matchingGrade.color);

    /**
     * Label 3
     */

    let label3 = chart.radarContainer.createChild(am4core.Label);
    label3.isMeasured = false;
    label3.fontSize = "0.7rem";
    label3.horizontalCenter = "middle";
    label3.verticalCenter = "bottom";
    label3.text = data.updatedDate;
    label3.fill = am4core.color(matchingGrade.color);

    window.addEventListener("resize", function () {
      let vw = window.innerWidth;

      if (vw > 1920) {
        label.fontSize = "1.6rem";
        psiLabel.fontSize = "1.3em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      } else {
        label.fontSize = "1.4rem";
        psiLabel.fontSize = "1em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      }
    });

    /**
     * Hand
     */

    let hand = chart.hands.push(new am4charts.ClockHand());
    hand.axis = axis2;
    hand.innerRadius = am4core.percent(55);
    hand.startWidth = 8;
    hand.pin.disabled = true;
    // hand.value = data.score;
    hand.fill = am4core.color("#444");
    hand.stroke = am4core.color("#000");

    setTimeout(() => {
      hand.showValue(data.score, 1000, am4core.ease.cubicOut); // animated
    }, 2000);

    removeAmChartLogo(this.elementRef.nativeElement);
  }

  chartAnnulusD() {
    // Check if mawop.annDPress is null or 0
    if (!this.mawop.annDPress || this.mawop.annDPress === 0) {
      // Clear the chart and return early
      this.noMawopChart("chartAnullusD", this.aprMeter.annDPress)
      return
    }

    let chartMin = 0;
    let chartMax = this.aprMeter.annDPress > this.mawop.annDPress + 500 ? this.aprMeter.annDPress : this.mawop.annDPress + 500;
    let eighttyPercentAnnD = this.mawop.annDPress * 0.8;


    let data = {
      score: this.aprMeter.annDPress ?? 0,
      updatedDate: handleFormatDate(this.activityDate, true) || '',
      fromAprCat: this.aprCategory ?? '',
      gradingData: [
        {
          title: "Healthy",
          color: "#7fd26cff",
          lowScore: -1,
          highScore: 200
        },
        {
          title: "Healthy Medium Risk",
          color: "#f3eb0c",
          lowScore: 200,
          highScore: eighttyPercentAnnD
        },
        {
          title: "Unhealthy High Risk",
          color: "#fdae19",
          lowScore: eighttyPercentAnnD,
          highScore: this.mawop.annDPress
        },
        {
          title: "Unhealthy Very High Risk",
          color: "#ee1f25",
          lowScore: this.mawop.annDPress,
          highScore: chartMax
        },
      ]
    };

    /**
    Grading Lookup
     */
    function lookUpGrade(lookupScore, grades) {
      // Only change code below this line
      for (var i = 0; i < grades.length; i++) {
        if (
          grades[i].lowScore < lookupScore &&
          grades[i].highScore >= lookupScore
        ) {
          return grades[i];
        }
      }
      return null;
    }

    // create chart
    let chart = am4core.create("chartAnullusD", am4charts.GaugeChart);
    chart.hiddenState.properties.opacity = 0;
    chart.fontSize = 7;
    chart.innerRadius = am4core.percent(80);
    chart.resizable = true;

    /**
     * Normal axis
     */

    const axis = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis.min = chartMin;
    axis.max = chartMax;
    axis.strictMinMax = true;
    axis.renderer.radius = am4core.percent(80);
    axis.renderer.inside = true;
    axis.renderer.line.strokeOpacity = 0.1;
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.strokeOpacity = 1;
    axis.renderer.ticks.template.strokeWidth = 0.5;
    axis.renderer.ticks.template.length = 5;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = am4core.percent(15);
    axis.renderer.labels.template.fontSize = "0.9em";
    axis.renderer.labels.template.fill = am4core.color("#000");


    /**
     * Axis for ranges
     */

    const axis2 = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis2.min = chartMin;
    axis2.max = chartMax;
    axis2.strictMinMax = true;
    axis2.renderer.labels.template.disabled = true;
    axis2.renderer.ticks.template.disabled = true;
    axis2.renderer.grid.template.disabled = false;
    axis2.renderer.grid.template.opacity = 0.5;
    axis2.renderer.labels.template.bent = true;
    axis2.renderer.labels.template.fill = am4core.color("#000");
    axis2.renderer.labels.template.fontWeight = "bold";
    axis2.renderer.labels.template.fillOpacity = 0.3;


    /**
    Ranges
    */

    for (let grading of data.gradingData) {
      let range = axis2.axisRanges.create();
      range.axisFill.fill = am4core.color(grading.color);
      range.axisFill.fillOpacity = 0.8;
      range.axisFill.zIndex = -1;
      range.value = grading.lowScore > chartMin ? grading.lowScore : chartMin;
      range.endValue = grading.highScore < chartMax ? grading.highScore : chartMax;
      range.grid.strokeOpacity = 0;
      (range as any).stroke = am4core.color(grading.color).lighten(-0.1);
      // range.label.inside = true;
      // range.label.text = grading.title.toUpperCase();
      // range.label.inside = true;
      // range.label.location = 0.5;
      // range.label.inside = true;
      // (range.label as any).radius = am4core.percent(10);
      // range.label.paddingBottom = -5; // ~half font size
      // range.label.fontSize = "0.9em";
    }

    let matchingGrade = lookUpGrade(data.score, data.gradingData);

    /**
     * Label 1
     */

    let label = chart.radarContainer.createChild(am4core.Label);
    label.isMeasured = false;
    label.fontSize = "1.4rem";
    label.x = am4core.percent(50);
    label.paddingBottom = 37;
    label.horizontalCenter = "middle";
    label.verticalCenter = "bottom";
    label.text = data.score.toFixed(0);
    label.fill = am4core.color(matchingGrade.color);


    /**
   * psiLabel
   */

    let psiLabel = chart.radarContainer.createChild(am4core.Label);
    psiLabel.isMeasured = false;
    psiLabel.fontSize = "1em";
    psiLabel.paddingBottom = 31;
    psiLabel.horizontalCenter = "middle";
    psiLabel.verticalCenter = "bottom";
    psiLabel.text = "PSI"
    psiLabel.fill = am4core.color(matchingGrade.color);

    /**
     * Label 2
     */

    let label2 = chart.radarContainer.createChild(am4core.Label);
    label2.isMeasured = false;
    label2.fontSize = "0.7rem";
    label2.paddingBottom = 15;
    label2.horizontalCenter = "middle";
    label2.verticalCenter = "bottom";
    label2.text = data.fromAprCat.toUpperCase();
    label2.fill = am4core.color(matchingGrade.color);

    /**
     * Label 3
     */

    let label3 = chart.radarContainer.createChild(am4core.Label);
    label3.isMeasured = false;
    label3.fontSize = "0.7rem";
    label3.horizontalCenter = "middle";
    label3.verticalCenter = "bottom";
    label3.text = data.updatedDate;
    label3.fill = am4core.color(matchingGrade.color);

    window.addEventListener("resize", function () {
      let vw = window.innerWidth;

      if (vw > 1920) {
        label.fontSize = "1.6rem";
        psiLabel.fontSize = "1.3em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      } else {
        label.fontSize = "1.4rem";
        psiLabel.fontSize = "1em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      }
    });

    /**
     * Hand
     */

    let hand = chart.hands.push(new am4charts.ClockHand());
    hand.axis = axis2;
    hand.innerRadius = am4core.percent(55);
    hand.startWidth = 8;
    hand.pin.disabled = true;
    // hand.value = data.score;
    hand.fill = am4core.color("#444");
    hand.stroke = am4core.color("#000");

    setTimeout(() => {
      hand.showValue(data.score, 1000, am4core.ease.cubicOut); // animated
    }, 1500);

    removeAmChartLogo(this.elementRef.nativeElement);
  }

  noMawopChart(chartId, aprMeter) {
    let chartMin = 0;
    let chartMax = !aprMeter || aprMeter == 0 ? 100 : aprMeter


    let data = {
      score: !aprMeter || aprMeter == 0 ? "0" : aprMeter,
      updatedDate: "No MAWOP Value",
      fromAprCat: 'N/A',
      gradingData: [
        {
          title: "Healthy",
          color: "#aab4a8ff",
          lowScore: -1,
          highScore: chartMax
        },
      ]
    };

    /**
    Grading Lookup
     */
    function lookUpGrade(lookupScore, grades) {
      // Only change code below this line
      for (var i = 0; i < grades.length; i++) {
        if (
          grades[i].lowScore < lookupScore &&
          grades[i].highScore >= lookupScore
        ) {
          return grades[i];
        }
      }
      return null;
    }

    // create chart
    let chart = am4core.create(chartId, am4charts.GaugeChart);
    chart.hiddenState.properties.opacity = 0;
    chart.fontSize = 7;
    chart.innerRadius = am4core.percent(80);
    chart.resizable = true;

    /**
     * Normal axis
     */

    const axis = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis.min = chartMin;
    axis.max = chartMax;
    axis.strictMinMax = true;
    axis.renderer.radius = am4core.percent(80);
    axis.renderer.inside = true;
    axis.renderer.line.strokeOpacity = 0.1;
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.strokeOpacity = 1;
    axis.renderer.ticks.template.strokeWidth = 0.5;
    axis.renderer.ticks.template.length = 5;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = am4core.percent(15);
    axis.renderer.labels.template.fontSize = "0.9em";
    axis.renderer.labels.template.fill = am4core.color("#000");


    /**
     * Axis for ranges
     */

    const axis2 = chart.xAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererCircular>()
    );
    axis2.min = chartMin;
    axis2.max = chartMax;
    axis2.strictMinMax = true;
    axis2.renderer.labels.template.disabled = true;
    axis2.renderer.ticks.template.disabled = true;
    axis2.renderer.grid.template.disabled = false;
    axis2.renderer.grid.template.opacity = 0.5;
    axis2.renderer.labels.template.bent = true;
    axis2.renderer.labels.template.fill = am4core.color("#000");
    axis2.renderer.labels.template.fontWeight = "bold";
    axis2.renderer.labels.template.fillOpacity = 0.3;


    /**
    Ranges
    */

    for (let grading of data.gradingData) {
      let range = axis2.axisRanges.create();
      range.axisFill.fill = am4core.color(grading.color);
      range.axisFill.fillOpacity = 0.8;
      range.axisFill.zIndex = -1;
      range.value = grading.lowScore > chartMin ? grading.lowScore : chartMin;
      range.endValue = grading.highScore < chartMax ? grading.highScore : chartMax;
      range.grid.strokeOpacity = 0;
      (range as any).stroke = am4core.color(grading.color).lighten(-0.1);
      // range.label.inside = true;
      // range.label.text = grading.title.toUpperCase();
      // range.label.inside = true;
      // range.label.location = 0.5;
      // range.label.inside = true;
      // (range.label as any).radius = am4core.percent(10);
      // range.label.paddingBottom = -5; // ~half font size
      // range.label.fontSize = "0.9em";
    }

    let matchingGrade = lookUpGrade(data.score, data.gradingData);

    /**
   * Label 1
   */

    let label = chart.radarContainer.createChild(am4core.Label);
    label.isMeasured = false;
    label.fontSize = "1.4rem";
    label.x = am4core.percent(50);
    label.paddingBottom = 37;
    label.horizontalCenter = "middle";
    label.verticalCenter = "bottom";
    label.text = data.score;
    // label.hidden = data.score == 0
    label.fill = am4core.color(matchingGrade.color);


    /**
   * psiLabel
   */

    let psiLabel = chart.radarContainer.createChild(am4core.Label);
    psiLabel.isMeasured = false;
    psiLabel.fontSize = "1em";
    psiLabel.paddingBottom = 31;
    psiLabel.horizontalCenter = "middle";
    psiLabel.verticalCenter = "bottom";
    psiLabel.text = "PSI"
    // psiLabel.hidden = data.score == 0
    psiLabel.fill = am4core.color(matchingGrade.color);

    /**
     * Label 2
     */

    let label2 = chart.radarContainer.createChild(am4core.Label);
    label2.isMeasured = false;
    label2.fontSize = "0.7rem";
    label2.paddingBottom = 15;
    label2.horizontalCenter = "middle";
    label2.verticalCenter = "bottom";
    label2.text = data.fromAprCat.toUpperCase();
    label2.fill = am4core.color(matchingGrade.color);

    /**
     * Label 3
     */

    let label3 = chart.radarContainer.createChild(am4core.Label);
    label3.isMeasured = false;
    label3.fontSize = "0.7rem";
    label3.horizontalCenter = "middle";
    label3.verticalCenter = "bottom";
    label3.text = data.updatedDate;
    label3.fill = am4core.color(matchingGrade.color);

    window.addEventListener("resize", function () {
      let vw = window.innerWidth;

      if (vw > 1920) {
        label.fontSize = "1.6rem";
        psiLabel.fontSize = "1.3em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      } else {
        label.fontSize = "1.4rem";
        psiLabel.fontSize = "1em";
        label2.fontSize = "0.7rem";
        label3.fontSize = "0.7rem";
      }
    });

    /**
     * Hand
     */

    let hand = chart.hands.push(new am4charts.ClockHand());
    hand.axis = axis2;
    hand.innerRadius = am4core.percent(55);
    hand.startWidth = 8;
    hand.pin.disabled = true;
    // hand.value = data.score;
    hand.fill = am4core.color("#444");
    hand.stroke = am4core.color("#000");

    setTimeout(() => {
      hand.showValue(data.score, 1000, am4core.ease.cubicOut); // animated
    }, 1500);

    removeAmChartLogo(this.elementRef.nativeElement);
  }

}
